// @ts-strict-ignore
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { ProjectModel } from '@project/model/project.model';
import { ArticleManager } from '@article/manager/article.manager';
import { ArticleTypeConstants } from '@article/constants/article-type-constants';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { Dictionary } from 'typescript-collections';
import { ArticleUtils } from '@article/util/article-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { MetaElementUsageVO, MetaElementVO, SimpleTextValueVO, TableTextItemVO, TableTextValueVO, TextureAreaVO, TextValueVO } from '@shared/vo/meta-element.vo';
import { StringUtils } from '@shared/util/string-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Connection } from '@3d-scene/model3d/connection';
import { ProjectVO } from '@project/model/vo/project.vo';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { PrintLabelingArticleInfo } from '@project/components/article/model/print-labeling-article-info';
import { PrintLabelingVO } from '@project/components/label/print-labeling/print-labeling.vo';
import { TextureConnectionInfoVO } from '@3d-scene/processor/texture-connection-info.vo';
import { TextureConnectionSortGroupHelper } from '@3d-scene/helper/connection/texture-connection-sort-group.helper';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { PrintLabelingDataService } from './print-labeling-data.service';
import { FileUtils } from '@shared/util/file-utils';
import { EventUtils } from '@shared/util/event-utils';
import { OverlayService } from '@shared/overlay/overlay.service';
import { OverlayBackground } from '@shared/overlay/overlay.type';
import { SmartScriptVersion } from './print-labeling.component';
import { PropertyUtils } from '@article/util/property-utils';
import { MetaElementBehaviorPropertyDef } from '@project/constants/meta-element-behavior-property-def';
import { UserSettings } from '@user/model/user-settings';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { TerminalBlockManager } from '../../../manager/terminal-block.manager';
import { ArticleTypeIds } from '@article/constants/article-type-ids';
import { Injectable } from '@angular/core';
import { ArticleBehaviorPropertyDef } from '@article/constants/article-behavior-property-def';
import { ProjectArticleService } from '@project/service/project-article.service';
import { ValidationService } from '@project/manager/validation.service';
import { ProjectService } from '../../import-export/service/project.service';
import { ArticleUsageService } from '@article/service/article-usage.service';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { MetaElementUsageUtils } from '../../../util/meta-element-usage-utils';
import { IsProjectType } from '../../../model/is-project-type';
import { lastValueFrom } from 'rxjs';
import { Log } from '@shared/debug/log';
import { UserModel } from '@user/model/user.model';
import { TextureConnectionInfo } from '@3d-scene/model3d/texture-connection-info';
import { Matrix3, Object3D, Vector3 } from 'three';

class TextureConnectionsInTerminalBlock {
  private _textureConnections: Connection[] = [];
  private _textureArticleUsageIds: string[] = [];

  constructor(private _terminalBlockArticleUsageIds: string[]) {
  }

  public get textureConnections(): Connection[] {
    return this._textureConnections;
  }

  public addTextureConnection(textureConnection: Connection): void {
    this._textureConnections.push(textureConnection);
    if (this._textureArticleUsageIds.indexOf(textureConnection.articleContainer.articleGUID) < 0) {
      this._textureArticleUsageIds.push(textureConnection.articleContainer.articleGUID);
    }
  }

  public containsTextureArticle(textureArticleContainer: ArticleContainer) {
    return this._textureArticleUsageIds.indexOf(textureArticleContainer.articleGUID) >= 0;
  }

  public containsTerminalBlockArticle(terminalBlock: ArticleContainer) {
    return terminalBlock && this._terminalBlockArticleUsageIds.indexOf(terminalBlock.articleGUID) >= 0;
  }
}

@Injectable({providedIn: 'root'})
export class PrintLabelingGenerator {
  private static readonly URL_PROTOCOL_SMART_SCRIPT_3: string = 'smartScript:';
  private static readonly URL_PROTOCOL_SMART_SCRIPT_4: string = 'smartScript4:';
  private static readonly MAX_CHARS_IN_ROW: number = 10;
  private static readonly ROW_SEPARATOR: string = '++';
  private static readonly GROUP_SEPARATOR: string = ' ';

  private _excludedCharCodes: string = '0x252C,0x2534,0xE000-0xE06C';
  private _excludedChars: any[] = null;

  constructor(
    private userSettings: UserSettings,
    private userModel: UserModel,
    public projectModel: ProjectModel,
    private validationService: ValidationService,
    private projectArticleService: ProjectArticleService,
    public articleManager: ArticleManager,
    private terminalBlockManager: TerminalBlockManager,
    public alertHelper: AlertHelper,
    public textureConnectionSortGroupHelper: TextureConnectionSortGroupHelper,
    private printLabelingService: PrintLabelingDataService,
    private overlayService: OverlayService,
    private projectService: ProjectService,
    private articleContainerUtils: ArticleContainerUtils,
    private containerService: ContainerService,
    private articleUsageService: ArticleUsageService
  ) {
  }

  public createPrintLabelingByProject(project: ProjectVO) {
    const articleUsage: ArticleUsageVO = ProjectUtils.getFirstArticleUsage(project);
    const labelingArticle: ArticleVO = this.articleUsageService.getArticleByArticleUsage(articleUsage);
    return this.createPrintLabelingByArticle(labelingArticle);
  }

  public createPrintLabelingByArticle(labelingArticle: ArticleVO): PrintLabelingVO {
    if (!labelingArticle) {
      return null;
    }
    const articleVersions: number[] = [labelingArticle.version];
    const printLabeling: PrintLabelingVO = new PrintLabelingVO();
    printLabeling.textCheckClass = this.getTextCheckClassProperty(labelingArticle);
    printLabeling.articleNo = labelingArticle.articleNo;
    printLabeling.project = this.projectModel.currentProject;
    printLabeling.exported = this.getExportedTimestamp();
    printLabeling.articleIdentity = labelingArticle.identity;
    printLabeling.articleVersions = articleVersions;
    printLabeling.textureConnectionInfos = this.getTextureConnectionsOnStrip(labelingArticle.identity, articleVersions);
    printLabeling.imageConnectionInfos = this.getImageConnectionsOnStrip(labelingArticle.identity, articleVersions);
    return printLabeling;
  }

  public exportPrintLabeling(labelingArticleInfo: PrintLabelingArticleInfo[], saveModeEnabled: boolean, selectedCutmarkOption: string = 'NONE'): void {
    if (!labelingArticleInfo) {
      return;
    }
    this.overlayService.requestOverlayShow(OverlayBackground.color, true);

    setTimeout(() => {
      const printLabeling: PrintLabelingVO = this.generatePrintLabelingVO(labelingArticleInfo, selectedCutmarkOption);
      this.sendPrintLabelingVoRequest(printLabeling, saveModeEnabled, labelingArticleInfo[0]);
    }, 0);
  }

  public generatePrintLabelingVO(labelingArticleInfos: PrintLabelingArticleInfo[], selectedCutmarkOption: string): PrintLabelingVO {
    this.validationService.updateRepresentationOfCurrentProjectForValidationPurposes();
    const printLabeling: PrintLabelingVO = new PrintLabelingVO();
    printLabeling.forDirectPrinting = false;
    printLabeling.articleUsageIds = [];
    printLabeling.textureConnectionInfos = [];
    printLabeling.imageConnectionInfos = [];
    const labelingArticle: ArticleVO = this.articleManager.getCachedArticleByArticleNo(labelingArticleInfos[0].articleNo);
    printLabeling.textCheckClass = this.getTextCheckClassProperty(labelingArticle);
    printLabeling.articleNo = labelingArticleInfos[0].articleNo;
    printLabeling.project = this.sortArticleUsagesInProject(labelingArticleInfos[0].articleNo, this.projectModel.currentProject);
    for (const info of labelingArticleInfos) {
      const fetchedTextureConnections = this.getTextureConnectionsOnStrip(info.articleId, info.articleVersions)
      ArrayUtils.mergeArrays(printLabeling.textureConnectionInfos, fetchedTextureConnections);

      const fetchedImageConnections = this.getImageConnectionsOnStrip(info.articleId, info.articleVersions)
      ArrayUtils.mergeArrays(printLabeling.imageConnectionInfos, fetchedImageConnections);

      ArrayUtils.mergeArrays(printLabeling.articleUsageIds, info.articleUsageIds);
    }
    //articleUsageIds needs to be null if a whole material is selected for the backend to fill the usages correctly
    if (printLabeling.articleUsageIds.length === 0) {
      printLabeling.articleUsageIds = null;
    }
    printLabeling.articleIdentity = labelingArticleInfos[0].articleId;
    printLabeling.articleVersions = labelingArticleInfos[0].articleVersions;
    printLabeling.usePlotter = false;
    printLabeling.selectedCutmarkOption = selectedCutmarkOption;
    return printLabeling;
  }

  public createPrintLabelingVOForDirectPrinting(): PrintLabelingVO {
    this.validationService.updateRepresentationOfCurrentProjectForValidationPurposes();
    const printLabeling: PrintLabelingVO = new PrintLabelingVO();
    printLabeling.forDirectPrinting = true;
    printLabeling.textureConnectionInfos = this.getAllTextureConnections();
    printLabeling.imageConnectionInfos = this.getAllImageConnections();
    printLabeling.project = this.projectModel.currentProject;
    printLabeling.articleUsageIds = null;
    printLabeling.usePlotter = false;
    return printLabeling;
  }

  private getExportedTimestamp(): string {
    const exported: string = this.projectModel.currentProject.exported;
    return this.userModel.checkPermissions(["PRINT_CAE_QR_CODE"]) ? exported : "";
  }

  private async sendPrintLabelingVoRequest(printLabeling: PrintLabelingVO, saveModeEnabled: boolean, firstLabelingArticleInfo: PrintLabelingArticleInfo): Promise<void> {
    try {
      const labelingData = await lastValueFrom(this.printLabelingService.printLabeling(printLabeling, saveModeEnabled))
      this.overlayService.requestOverlayHide(OverlayBackground.color);
      this.labelsPrinted_success({
        saveModeEnabled,
        label: firstLabelingArticleInfo.label + firstLabelingArticleInfo.labelForSubNodes,
        articleNo: printLabeling.articleNo,
        labelingData,
        silent: !StringUtils.isEmpty(printLabeling.exported),
      });
    } catch (error) {
      Log.error("Could not create wscx file for print labeling", error);
      this.overlayService.requestOverlayHide(OverlayBackground.color);
    }
  }

  public printSelectedPrintLabeling(articleUsageIds: string[]): void {
    if (ArrayUtils.isEmpty(articleUsageIds)) {
      return;
    }
    const labelingArticleInfo: PrintLabelingArticleInfo = new PrintLabelingArticleInfo();
    const articleUsages: ArticleUsageVO[] = this.projectModel.getArticleUsages(articleUsageIds);
    const articleVersions: number[] = [];
    for (const articleUsage of articleUsages) {
      const articleVersion: number = articleUsage.articleVersion;
      if (articleVersions.indexOf(articleVersion) === -1) {
        articleVersions.push(articleVersion);
      }
    }
    const articleUsage: ArticleUsageVO = articleUsages[0];
    const article: ArticleVO = this.articleUsageService.getArticleByArticleUsage(articleUsage);
    labelingArticleInfo.articleNo = article.articleNo;
    labelingArticleInfo.label = article.articleNo;
    labelingArticleInfo.articleId = articleUsage.articleId;
    labelingArticleInfo.articleUsageIds = articleUsageIds;
    labelingArticleInfo.articleVersions = articleVersions;
    this.exportPrintLabeling([labelingArticleInfo], false);
  }

  public collectExcludedCharCodes(): void {
    const ranges: string[] = this._excludedCharCodes.split(',');
    if (ArrayUtils.isEmpty(ranges)) {
      return;
    }
    this._excludedChars = [];
    for (const range of ranges) {
      const charCodes: string[] = range.split('-');
      if (charCodes.length === 1) {
        this._excludedChars.push(charCodes[0]);
      } else {
        for (let charCode = +charCodes[0]; charCode <= +charCodes[1]; charCode++) {
          this._excludedChars.push(charCode);
        }
      }
    }
  }


  private getTextCheckClassProperty(labelingArticle: ArticleVO): string {
    const markingBorderTextCheckClass: string = 'MARKINGBORDER';
    let textCheckClasses: string[] = labelingArticle.metaElements.map((meta) => {
      if (meta instanceof TextureAreaVO && PropertyUtils.containsProperty(meta.behaviorProperties, MetaElementBehaviorPropertyDef.TEXT_CHECK_CLASS)) {
        return PropertyUtils.getStringPropertyValue(meta.behaviorProperties, MetaElementBehaviorPropertyDef.TEXT_CHECK_CLASS);
      }
    });
    textCheckClasses = textCheckClasses.filter((e) => e && e !== markingBorderTextCheckClass);

    const textCheckClass: string = textCheckClasses[0];
    const isMoreThanOneTextCheckClass: boolean = textCheckClasses.some((e) => e !== textCheckClass);

    return isMoreThanOneTextCheckClass || ArrayUtils.isEmpty(textCheckClasses) ? '' : textCheckClass;
  }

  private sortArticleUsagesInProject(articleNo: string, project: ProjectVO): ProjectVO {
    if (!articleNo || ProjectUtils.isEmptyProject(project)) {
      return project;
    }

    for (const composition of project.compositions) {
      const articleUsages: ArticleUsageVO[] = composition.articleUsages;
      this.sortArticleUsagesByArticleNumber(articleNo, articleUsages);
    }
    return project;
  }

  private sortArticleUsagesByArticleNumber(articleNo: string, articleUsages: ArticleUsageVO[]): void {
    if (ArrayUtils.isEmpty(articleUsages)) {
      return;
    }

    const textureConnections: Connection[] = [];
    for (let i = 0; i < articleUsages.length; i++) {
      const articleUsage: ArticleUsageVO = articleUsages[i];
      const article: ArticleVO = this.articleUsageService.getArticleByArticleUsage(articleUsage);
      if (!article || article.articleNo !== articleNo) {
        continue;
      }

      const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleUsage.id);
      for (const textureAreaUsage of ArticleUsageUtils.textureAreaUsages(articleUsage)) {
        const connection: Connection = articleContainer.findFirstConnectionByName(textureAreaUsage.identity.metaElementName);
        if (connection) {
          textureConnections.push(connection);
          if (articleUsages.indexOf(articleUsage) > -1) {
            articleUsages.splice(i, 1);
            i--;
          }
        }
      }
    }

    let textureConnectionGroups: Connection[][] = this.textureConnectionSortGroupHelper.splitConnectionsForPrint(textureConnections);
    textureConnectionGroups = this.splitConnectionGroupsByTerminalBlocks(textureConnectionGroups);
    textureConnectionGroups.sort((a: Connection[], b: Connection[]) => this.groupSortFunction(a, b));
    for (const sortGroup of textureConnectionGroups) {
      this.sortTextureUsagesInArticle(sortGroup);
      for (const sortedConnection of sortGroup) {
        const usage: ArticleUsageVO = sortedConnection.articleContainer.articleUsage;
        if (articleUsages.indexOf(usage) < 0) {
          articleUsages.push(usage);
        }
      }
    }
  }

  private splitConnectionGroupsByTerminalBlocks(textureConnectionGroups: Connection[][]): Connection[][] {
    if (!IsProjectType.mounting(this.projectModel.currentProject)) {
      // terminal blocks can be determined only in mounting-rail project
      return textureConnectionGroups;
    }

    const result: Connection[][] = [];
    for (const connectionGroup of textureConnectionGroups) {
      result.push(...this.splitConnectionGroupByTerminalBlocks(connectionGroup));
    }
    return result;
  }

  private splitConnectionGroupByTerminalBlocks(textureConnectionGroup: Connection[]): Connection[][] {
    const terminalBlocks: TextureConnectionsInTerminalBlock[] = [];
    let currentTerminalBlock: TextureConnectionsInTerminalBlock;
    for (const textureConnection of textureConnectionGroup) {
      const textureArticleContainer: ArticleContainer = textureConnection.articleContainer;
      const existingBlock: TextureConnectionsInTerminalBlock = terminalBlocks.find((block) => block.containsTextureArticle(textureArticleContainer));
      if (existingBlock) {
        // some texture-connection of the given labeling-article is already processed and included in the terminal-block
        // - include all remaining texture-connection in the same terminal-block
        existingBlock.addTextureConnection(textureConnection);
      } else {
        const articleContainerOnRail: ArticleContainer =
          this.articleContainerUtils.getBackwardArticleContainerOnRailRecursively(textureArticleContainer);
        if (currentTerminalBlock && currentTerminalBlock.containsTerminalBlockArticle(articleContainerOnRail)) {
          currentTerminalBlock.addTextureConnection(textureConnection);
        } else {
          currentTerminalBlock = this.initTerminalBlock(articleContainerOnRail);
          currentTerminalBlock.addTextureConnection(textureConnection);
          terminalBlocks.push(currentTerminalBlock);
        }
      }
    }
    return terminalBlocks.map((block) => block.textureConnections);
  }

  private initTerminalBlock(articleContainerOnRail: ArticleContainer): TextureConnectionsInTerminalBlock {
    const terminalBlockContainers: ArticleContainer[] = this.terminalBlockManager.getArticleContainersInTerminalBlock(articleContainerOnRail, [
      ArticleTypeIds.BLOCKING_ZONE,
      ArticleTypeIds.EMPTY_ZONE,
      ArticleTypeIds.END_TERMINAL,
    ]);
    return new TextureConnectionsInTerminalBlock(terminalBlockContainers.map((container) => container.articleGUID));
  }

  private sortTextureUsagesInArticle(sortGroup: Connection[]): void {
    const articleUsageIdToTextureConnections: Dictionary<string, Connection[]> = this.groupTextureConnectionsByArticle(sortGroup);
    for (const articleUsageId of articleUsageIdToTextureConnections.keys()) {
      const connectionsFromArticle: Connection[] = articleUsageIdToTextureConnections.getValue(articleUsageId);
      if (connectionsFromArticle && connectionsFromArticle.length > 1) {
        const articleUsage: ArticleUsageVO = connectionsFromArticle[0].articleContainer.articleUsage;
        const metaElementUsages: MetaElementUsageVO[] = this.getMetaElementUsagesFromConnections(connectionsFromArticle);
        if (ArticleUsageUtils.containsMetaElementUsages(articleUsage, metaElementUsages)) {
          ArticleUsageUtils.removeMetaElementUsages(articleUsage, metaElementUsages);
          ArticleUsageUtils.addMetaElementUsages(articleUsage, metaElementUsages);
        }
      }
    }
  }

  private groupTextureConnectionsByArticle(sortGroup: Connection[]): Dictionary<string, Connection[]> {
    const articleIdToTextureConnections: Dictionary<string, Connection[]> = new Dictionary<string, Connection[]>();
    for (const connectionFromGroup of sortGroup) {
      const articleGUID: string = connectionFromGroup.articleContainer.articleGUID;
      let connectionsFromArticle: Connection[] = articleIdToTextureConnections.getValue(articleGUID);
      if (!connectionsFromArticle) {
        connectionsFromArticle = [];
        articleIdToTextureConnections.setValue(articleGUID, connectionsFromArticle);
      }
      connectionsFromArticle.push(connectionFromGroup);
    }
    return articleIdToTextureConnections;
  }

  private getMetaElementUsagesFromConnections(connectionsFromArticle: Connection[]): MetaElementUsageVO[] {
    const metaElementUsages: MetaElementUsageVO[] = [];
    for (const connectionFromArticle of connectionsFromArticle) {
      metaElementUsages.push(connectionFromArticle.metaElementUsage);
    }
    return metaElementUsages;
  }

  private groupSortFunction(a: Connection[], b: Connection[]): number {
    return MathUtils.compare(this.getGroupDistanceToSortPoint(a[0]), this.getGroupDistanceToSortPoint(b[0]));
  }

  private getGroupDistanceToSortPoint(connection: Connection): number {
    const rectangle: Rectangle3D = connection.globalRectangle;
    return LabelingUtils.POINT_TO_SORT.distanceTo(rectangle.point1);
  }

  /** @returns all textureconnections that sit on a specific labelingstrip (identified by articleIdentity and articleVersions) */
  private getTextureConnectionsOnStrip(articleIdentity: number, articleVersions: number[]): TextureConnectionInfoVO[] {
    return this.containerService
      .enumerateArticleContainersInGlobal()
      .filter(container => container.articleUsage.articleId === articleIdentity
        && (!articleVersions || articleVersions.includes(container.articleUsage.articleVersion)))
      .flatMap(container => this.collectConnectionInfoVOs(container, container.textConnections));
  }

  private getImageConnectionsOnStrip(articleIdentity: number, articleVersions: number[]): TextureConnectionInfoVO[] {
    return this.containerService
      .enumerateArticleContainersInGlobal()
      .filter(container => container.articleUsage.articleId === articleIdentity
        && (!articleVersions || articleVersions.includes(container.articleUsage.articleVersion)))
      .flatMap(container => this.collectConnectionInfoVOs(container, container.imageConnections));
  }

  /** @returns all textureconnections in the project */
  private getAllTextureConnections() {
    return this.containerService
      .enumerateArticleContainersInGlobal()
      .flatMap(ac => this.collectConnectionInfoVOs(ac, ac.textConnections));
  }

  private getAllImageConnections() {
    return this.containerService
      .enumerateArticleContainersInGlobal()
      .flatMap(ac => this.collectConnectionInfoVOs(ac, ac.imageConnections));
  }

  private collectConnectionInfoVOs(articleContainer: ArticleContainer, connections: TextureConnectionInfo[]): TextureConnectionInfoVO[] {
    return (connections || [])
      .map(connection => this.createConnectionInfoVO(articleContainer, connection))
  }

  private createConnectionInfoVO(articleContainer: ArticleContainer, textConnection: TextureConnectionInfo): TextureConnectionInfoVO {
    const textConnectionInfoVO: TextureConnectionInfoVO = new TextureConnectionInfoVO();
    textConnectionInfoVO.id = textConnection.id;
    textConnectionInfoVO.articleUsageId = articleContainer.articleUsage.id;
    textConnectionInfoVO.connectionWidth = textConnection.connectionWidth;
    textConnectionInfoVO.connectionHeight = textConnection.connectionHeight;
    const connection = articleContainer.connections.find(c => c.id == textConnection.id);

    const connectionWorldPos = connection.mesh.localToWorld(connection.meshPosition);
    textConnectionInfoVO.positionX = -connectionWorldPos.x;

    textConnectionInfoVO.distanceToPreviousConnection = MathUtils.roundNumberValue(textConnection.distanceToPreviousConnection, 1);
    const metaElement: MetaElementVO = ArticleUtils.findMetaElementByName(articleContainer.article, textConnection.id);
    textConnectionInfoVO.corridor = !metaElement ? NaN : metaElement.corridor;

    textConnectionInfoVO.rotation = connection.getMeshRotationDegree(false);
    textConnectionInfoVO.normalVector = this.toWorldNormal(connection.connectorNormal, connection.mesh);

    textConnectionInfoVO.isDirectPrint = !ArticleUtils.isLabelingArticle(articleContainer.article) && ConnectionInfoUtils.isPartOfDefaultLabeling(metaElement);

    textConnectionInfoVO.isRotationInverted = articleContainer.rotation.y > 0
      ? textConnection.rectangle.point1.z > textConnection.rectangle.point3.z
      : textConnection.rectangle.point1.z < textConnection.rectangle.point3.z;

    textConnectionInfoVO.type = textConnection.type;
    return textConnectionInfoVO;
  }

  private toWorldNormal(localNormal: Vector3, mesh: Object3D): Vector3 {
    const normalMatrix = new Matrix3().getNormalMatrix(mesh.matrixWorld);
    return localNormal.clone().applyNormalMatrix(normalMatrix).normalize();
  }

  private excludeChars(label: string): string {
    if (ArrayUtils.isEmpty(this._excludedChars)) {
      return label;
    }
    let labelWithExcludedChars: string = '';
    for (let i = 0; i < label.length; i++) {
      const charCode: number = label.charCodeAt(i);
      if (this._excludedChars.indexOf(charCode) === -1) {
        labelWithExcludedChars += String.fromCharCode(charCode);
      } else {
        labelWithExcludedChars += '_';
      }
    }
    return labelWithExcludedChars;
  }

  private getArticleByArticleUsage(articleUsage: ArticleUsageVO): ArticleVO {
    return this.articleUsageService.getArticleByArticleUsage(articleUsage);
  }

  public getPrintLabelingArticleInfosForDataProvider() {
    const labelingArticleInfos: PrintLabelingArticleInfo[] = [];
    const labelingArticleUsages: ArticleUsageVO[] = this.projectArticleService.findArticleUsagesByType(ArticleTypeConstants.TYPE_LABELING);
    const articleNumberToDynamicLabelingArticleUsages: Dictionary<string, ArticleUsageVO[]> = new Dictionary<string, ArticleUsageVO[]>();
    const articleNumberToNonDynamicLabelingArticleUsages: Dictionary<string, ArticleUsageVO[]> = new Dictionary<string, ArticleUsageVO[]>();
    for (const labelingArticleUsage of labelingArticleUsages) {
      const labelingArticle: ArticleVO = this.getArticleByArticleUsage(labelingArticleUsage);
      if (!labelingArticle || ArticleUtils.isExcludedFromPrintLabeling(labelingArticle)) {
        continue;
      }
      if (ArticleUtils.isDynamicLabelingArticle(labelingArticle)) {
        this.addLabelingArticleUsage(labelingArticle.articleNo, labelingArticleUsage, articleNumberToDynamicLabelingArticleUsages);
      } else {
        this.addLabelingArticleUsage(labelingArticle.articleNo, labelingArticleUsage, articleNumberToNonDynamicLabelingArticleUsages);
      }
    }

    this.sortDynamicLabelingArticleUsages(articleNumberToDynamicLabelingArticleUsages);

    this.addDynamicLabelingArticlesToDataProvider(articleNumberToDynamicLabelingArticleUsages, labelingArticleInfos);

    this.addNonDynamicLabelingArticlesToDataProvider(articleNumberToNonDynamicLabelingArticleUsages, labelingArticleInfos);

    return labelingArticleInfos;
  }

  private addLabelingArticleUsage(articleNumber: string, labelingArticleUsage: ArticleUsageVO, storage: Dictionary<string, ArticleUsageVO[]>): void {
    if (!storage.containsKey(articleNumber)) {
      storage.setValue(articleNumber, []);
    }
    storage.getValue(articleNumber).push(labelingArticleUsage);
  }

  private sortDynamicLabelingArticleUsages(dynamicLabelingArticleUsages: Dictionary<string, ArticleUsageVO[]>): void {
    for (const articleNumber of dynamicLabelingArticleUsages.keys()) {
      const dynamicArticleUsages: ArticleUsageVO[] = dynamicLabelingArticleUsages.getValue(articleNumber);
      if (dynamicArticleUsages.length > 1) {
        this.sortArticleUsagesByArticleNumber(articleNumber, dynamicArticleUsages);
      }
    }
  }

  private addDynamicLabelingArticlesToDataProvider(
    articleNumberToDynamicLabelingArticleUsages: Dictionary<string, ArticleUsageVO[]>,
    labelingArticleInfos: PrintLabelingArticleInfo[]
  ): void {
    for (const dynamicArticleUsages of articleNumberToDynamicLabelingArticleUsages.values()) {
      const articleUsageVO: ArticleUsageVO = dynamicArticleUsages[0] as ArticleUsageVO;
      const dynamicPrintLabelingArticleInfo: PrintLabelingArticleInfo = this.createPrintLabelingByArticleInfo(articleUsageVO);
      dynamicPrintLabelingArticleInfo.articleVersions.push(articleUsageVO.articleVersion);
      if (dynamicArticleUsages.length > 1) {
        dynamicPrintLabelingArticleInfo.children = [];
        for (const articleUsage of dynamicArticleUsages) {
          const subNode: PrintLabelingArticleInfo = this.createPrintLabelingByArticleInfo(articleUsage);
          subNode.labelForSubNodes = this.getLabelFromArticleUsage(articleUsage);
          subNode.articleUsageIds = [articleUsage.id];
          subNode.articleVersions.push(articleUsage.articleVersion);
          dynamicPrintLabelingArticleInfo.children.push(subNode);
          if (dynamicPrintLabelingArticleInfo.articleVersions.indexOf(articleUsage.articleVersion) === -1) {
            dynamicPrintLabelingArticleInfo.articleVersions.push(articleUsage.articleVersion);
          }
        }
      }
      labelingArticleInfos.push(dynamicPrintLabelingArticleInfo);
    }
  }

  private addNonDynamicLabelingArticlesToDataProvider(
    nonDynamicLabelingArticleUsages: Dictionary<string, ArticleUsageVO[]>,
    labelingArticleInfos: PrintLabelingArticleInfo[]
  ): void {
    for (const nonDynamicArticleUsages of nonDynamicLabelingArticleUsages.values()) {
      const articleUsageVO: ArticleUsageVO = nonDynamicArticleUsages[0];
      const nonDynamicPrintLabelingArticleInfo: PrintLabelingArticleInfo = this.createPrintLabelingByArticleInfo(articleUsageVO);
      for (const articleUsage of nonDynamicArticleUsages) {
        if (nonDynamicPrintLabelingArticleInfo.articleVersions.indexOf(articleUsage.articleVersion) === -1) {
          nonDynamicPrintLabelingArticleInfo.articleVersions.push(articleUsage.articleVersion);
        }
      }
      labelingArticleInfos.push(nonDynamicPrintLabelingArticleInfo);
    }
  }

  private createPrintLabelingByArticleInfo(articleUsage: ArticleUsageVO): PrintLabelingArticleInfo {
    const labelingArticle: ArticleVO = this.getArticleByArticleUsage(articleUsage);
    const printLabelingArticleInfo: PrintLabelingArticleInfo = new PrintLabelingArticleInfo();
    printLabelingArticleInfo.articleNo = labelingArticle.articleNo;
    printLabelingArticleInfo.articleId = labelingArticle.identity;
    printLabelingArticleInfo.articleVersions = [];
    printLabelingArticleInfo.label = labelingArticle.articleNo;
    printLabelingArticleInfo.hasCutmarkAbility = PropertyUtils.getBooleanPropertyValue(labelingArticle.behaviorProperties, ArticleBehaviorPropertyDef.HAS_CUTMARK_ABILITY);
    return printLabelingArticleInfo;
  }

  private getLabelFromArticleUsage(articleUsage: ArticleUsageVO): string {
    const metaElementUsages: MetaElementUsageVO[] = articleUsage.metaElementUsages;
    if (ArrayUtils.isEmpty(metaElementUsages)) {
      return null;
    }
    // [ row1[gr1, gr2...],  row2[gr1, gr2, gr3...],  row3[gr1...] ]
    const labelRows: string[][] = [];
    for (const textureAreaUsage of MetaElementUsageUtils.textureAreaUsages(metaElementUsages)) {
      this.collectTextFromTextValue(textureAreaUsage.textValue, labelRows);
    }
    return this.buildLabel(labelRows);
  }

  private collectTextFromTextValue(textValue: TextValueVO, labelRows: string[][]): void {
    if (!textValue) {
      return;
    }
    if (textValue instanceof SimpleTextValueVO) {
      this.collectTextFromSimpleTextValue(textValue, labelRows);
    } else if (textValue instanceof TableTextValueVO) {
      this.collectTextFromTableTextValue(textValue, labelRows);
    }
  }

  private collectTextFromSimpleTextValue(simpleTextValue: SimpleTextValueVO, labelRows: string[][], rowNumber: number = 0): void {
    if (!simpleTextValue) {
      return;
    }
    let text: string = simpleTextValue.text;
    if (!StringUtils.isEmpty(text)) {
      text = StringUtils.convertFromFlexHtmlToString(text);
      if (!this.hasPrintableCharacters(text)) {
        return;
      }
      if (StringUtils.getNumLines(text) > 1) {
        const lines: string[] = text.split('\n');
        text = lines[0];
      }
      let row: string[] = labelRows[rowNumber];
      if (ArrayUtils.isEmpty(row)) {
        row = [];
        labelRows[rowNumber] = row;
      }
      row.push(text);
    }
  }

  private collectTextFromTableTextValue(tableTextValue: TableTextValueVO, labelRows: any[]): void {
    const rows = tableTextValue.rows;
    if (ArrayUtils.isEmpty(rows)) {
      return;
    }
    let rowNumber: number = 0;
    for (const tableTextRow of rows) {
      const items: TableTextItemVO[] = tableTextRow.items;
      if (ArrayUtils.isEmpty(items)) {
        return;
      }
      for (const tableTextItem of items) {
        this.collectTextFromSimpleTextValue(tableTextItem.value as SimpleTextValueVO, labelRows, rowNumber);
      }
      rowNumber++;
    }
  }

  private buildLabel(labelRows: string[][]): string {
    let label: string = ' (';
    for (let i = 0; i < labelRows.length; i++) {
      const labelGroups: string[] = labelRows[i];
      const isNotLastRow: boolean = i + 1 < labelRows.length;
      if (labelGroups) {
        let rowLimit: number = PrintLabelingGenerator.MAX_CHARS_IN_ROW;
        for (let j = 0; j < labelGroups.length; j++) {
          const labelGroup: string = labelGroups[j];
          const groupLength: number = labelGroup.length;
          if (groupLength > rowLimit) {
            const groupChars: string = labelGroup.slice(0, rowLimit);
            label += groupChars;
            if (isNotLastRow) {
              label += PrintLabelingGenerator.GROUP_SEPARATOR;
            }
            break;
          } else {
            label += labelGroup;
            rowLimit -= groupLength;
            if (j + 1 < labelGroups.length) {
              label += PrintLabelingGenerator.GROUP_SEPARATOR;
            }
          }
          if (isNotLastRow) {
            label += PrintLabelingGenerator.GROUP_SEPARATOR;
          }
        }
      }
      if (isNotLastRow) {
        label += PrintLabelingGenerator.ROW_SEPARATOR + PrintLabelingGenerator.GROUP_SEPARATOR;
      }
    }
    label += ')';
    return label;
  }

  private hasPrintableCharacters(text: string): boolean {
    for (let i = 0; i < text.length; i++) {
      const charCode: number = text.charCodeAt(i);
      if (StringUtils.isPrintableCharacter(charCode)) {
        return true;
      }
    }
    return false;
  }

  public labelsPrinted_success(data: {articleNo: string, label: string, labelingData: string, saveModeEnabled: boolean, silent: boolean }): void {
    let label: string = data.label;
    if (data.saveModeEnabled) {
      this.alertHelper
        .showAlert('printLabelingPopup.title.save_labeling_file', 'printLabelingPopup.message.save_labeling_file', null, [AlertButton.OK], "info")
        .then(() => {
          if (StringUtils.isEmpty(label)) {
            label = '';
          } else {
            label = this.excludeChars(label);
          }
          const filename: string = StringUtils.sanitizeFileName(this.projectService.getProjectName(this.projectModel.currentProject) + '_' + label);
          FileUtils.saveTextFile(filename, data.labelingData, '.wscx', false);
        });
    } else {
      let link: string = data.labelingData;
      const urlProtocol: string =
        this.userSettings.lastSelectedSmartScriptVersion === SmartScriptVersion.smartScript3
          ? PrintLabelingGenerator.URL_PROTOCOL_SMART_SCRIPT_3
          : PrintLabelingGenerator.URL_PROTOCOL_SMART_SCRIPT_4;
      if (urlProtocol === PrintLabelingGenerator.URL_PROTOCOL_SMART_SCRIPT_4) {
        // SMAR-397: slightly changed URL to call new smart-script (v4) using parameter /p (directly open print dialog) and -m before label material
        link = urlProtocol + '/p&' + this.host + link + '&-m' + data.articleNo;
      } else {
        link = urlProtocol + this.host + link + '&' + data.articleNo;
      }
      if (data.silent) {
        link += '&/silent';
      }
      this.callPrinterLink(link);
    }
  }

  private callPrinterLink(triggerLink: string): void {
    EventUtils.resetBeforeUnload();
    window.open(triggerLink, '_self');
    EventUtils.restoreBeforeUnload();
  }

  private get host(): string {
    const baseUrl: string = window.location.href;
    const trimmedUrl: string = this.beforeSlash(baseUrl, '^.*://[^/]*', '//');
    return trimmedUrl.replace(new RegExp('^.*://'), '');
  }

  private beforeSlash(value: string, patternString: string, defaultValue: string): string {
    let result: string;
    const pattern: RegExp = new RegExp(patternString);
    if (pattern.test(value) === true) {
      result = pattern.exec(value).toString();
    } else {
      result = defaultValue;
    }
    return result;
  }
}
